import React, { useEffect, useRef, useState } from "react";
import { Copy, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function ShareButton({ title, url, className = "" }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const closeOnScroll = () => setOpen(false);
    const closeOnWheel = () => setOpen(false);
    const closeOnTouchMove = () => setOpen(false);

    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    // Listen in capture phase for page and nested scroll containers.
    document.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("wheel", closeOnWheel, { passive: true, capture: true });
    document.addEventListener("touchmove", closeOnTouchMove, { passive: true, capture: true });
    window.addEventListener("resize", closeOnScroll);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      document.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("wheel", closeOnWheel, true);
      document.removeEventListener("touchmove", closeOnTouchMove, true);
      window.removeEventListener("resize", closeOnScroll);
    };
  }, [open]);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 8 });

  const getShareUrl = () => `${window.location.origin}${url}`;
  const getShareText = () => `${title} — PrintMyTees: ${getShareUrl()}`;

  const copyLink = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(getShareUrl());
      toast.success("Link copied");
      setOpen(false);
    } catch {
      toast.error("Could not copy link. Please copy the page URL from your browser.");
    }
  };

  const shareWhatsApp = (event) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(`https://wa.me/?text=${encodeURIComponent(getShareText())}`, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const nativeShare = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!navigator.share) return;
    try {
      await navigator.share({ title, text: `${title} — PrintMyTees`, url: getShareUrl() });
      setOpen(false);
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("Sharing could not be opened.");
    }
  };

  const positioningClass = /\b(?:absolute|fixed)\b/.test(className) ? "" : "relative";

  return (
    <div ref={containerRef} className={`${positioningClass} z-50 inline-flex h-11 w-11 shrink-0 items-center justify-center visible opacity-100 ${className}`} onClick={(event) => event.stopPropagation()}>
      <div className="relative inline-flex">
      <button
        type="button"
        onClick={(event) => { event.preventDefault(); event.stopPropagation(); const rect = event.currentTarget.getBoundingClientRect(); setMenuPosition({ top: Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - 180)), right: Math.max(8, Math.min(window.innerWidth - rect.right, window.innerWidth - 184)) }); setOpen((value) => !value); }}
        className="relative z-50 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-transparent p-0 text-[#141413] hover:text-[#D95328] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328]"
        aria-label={`Share ${title}`}
        aria-expanded={open}
        title="Share"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E6DFD5] bg-white shadow-sm hover:bg-[#F3EDE2]"><Share2 size={15} className="sm:h-4 sm:w-4" strokeWidth={2.2} /></span>
      </button>
      {open && (
        <div style={{ position: "fixed", top: menuPosition.top, right: menuPosition.right }} className="z-[100] w-44 max-w-[calc(100vw-16px)] rounded-lg border border-[#E6DFD5] bg-white p-1.5 shadow-lg">
          <button type="button" onClick={shareWhatsApp} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-[#F3EDE2]">
            <MessageCircle size={16} /> WhatsApp
          </button>
          <button type="button" onClick={copyLink} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-[#F3EDE2]">
            <Copy size={16} /> Copy link
          </button>
          {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
            <button type="button" onClick={nativeShare} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-[#F3EDE2]">
              <Share2 size={16} /> More options
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
